/**
 * iter257 — Corps d'un e-mail affiché proprement.
 *
 * - HTML : rendu dans une iframe isolée (aucun script exécuté, styles de
 *   l'e-mail sans effet sur le CRM), hauteur ajustée au contenu, images
 *   bloquées par défaut avec un bouton « Afficher les images ».
 * - Texte : citations de l'historique repliées derrière un « … ».
 */
import { useEffect, useRef, useState } from "react";
import { ImageOff, ChevronDown } from "lucide-react";

const QUOTE_SPLIT = /(?:^|\n)(?=(?:Le .{0,80}a écrit\s*:|On .{0,80}wrote:|-{2,}\s*Message d'origine|_{10,}|De\s*:\s.{0,80}\n))/;

const blockImages = (html) =>
  html
    .replace(/<img([^>]*?)\ssrc=/gi, "<img$1 data-blocked-src=")
    .replace(/background-image\s*:\s*url\([^)]*\)/gi, "");

export default function MailMessageBody({ html, text, testId }) {
  const [showImages, setShowImages] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(120);

  const hadImages = !!html && /<img/i.test(html);
  const srcDoc = html
    ? `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">
<style>
  html,body{margin:0;padding:0;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;color:#1f2937;word-break:break-word;}
  img{max-width:100%!important;height:auto!important;}
  table{max-width:100%!important;}
  a{color:#2563eb;}
  blockquote{margin:0 0 0 .75rem;padding-left:.75rem;border-left:2px solid #e5e7eb;color:#6b7280;}
</style></head><body>${showImages ? html : blockImages(html)}</body></html>`
    : null;

  useEffect(() => {
    if (!srcDoc) return;
    const id = setInterval(() => {
      const doc = iframeRef.current?.contentDocument;
      if (!doc?.body) return;
      const h = Math.min(4000, Math.max(80, doc.body.scrollHeight + 16));
      setHeight((prev) => (Math.abs(prev - h) > 4 ? h : prev));
    }, 300);
    return () => clearInterval(id);
  }, [srcDoc]);

  if (srcDoc) {
    return (
      <div data-testid={testId}>
        {hadImages && !showImages && (
          <button
            type="button"
            onClick={() => setShowImages(true)}
            className="mb-2 inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1 hover:bg-gray-100"
            data-testid={`${testId}-show-images`}
          >
            <ImageOff className="w-3.5 h-3.5" /> Images bloquées — afficher
          </button>
        )}
        <iframe
          ref={iframeRef}
          title="Message"
          srcDoc={srcDoc}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          className="w-full border-0"
          style={{ height }}
        />
      </div>
    );
  }

  const raw = (text || "").trim();
  const parts = raw.split(QUOTE_SPLIT);
  const main = parts[0] || "";
  const quoted = parts.slice(1).join("\n").trim();

  return (
    <div className="text-sm text-gray-800" data-testid={testId}>
      <div className="whitespace-pre-wrap break-words">{main}</div>
      {quoted && (
        <>
          <button
            type="button"
            onClick={() => setQuoteOpen((v) => !v)}
            className="mt-2 inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 hover:bg-gray-200"
            data-testid={`${testId}-toggle-quote`}
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${quoteOpen ? "rotate-180" : ""}`} />
            {quoteOpen ? "Masquer l'historique" : "Afficher l'historique"}
          </button>
          {quoteOpen && (
            <div className="mt-2 pl-3 border-l-2 border-gray-200 text-xs text-gray-500 whitespace-pre-wrap break-words">
              {quoted}
            </div>
          )}
        </>
      )}
    </div>
  );
}
